import { uniq } from "lodash";
import {
  getPreviewRecipientKey,
  isRegisteredPreviewSend,
  type DecisionEngineDestinationDefinition,
} from "./definition";
import {
  getMappingForUserColumn,
  getMappingForVariable,
  PREVIEW_TRACE_ID_VARIABLE,
} from "./sync-config-helpers";

// Marketo "my tokens" are flat scalars; trigger.json `tokens[].name` needs the
// wrapped {{my.<name>}} form. Names allow only alphanumerics, spaces, and & # _ -
// (no periods), so we use short underscore-delimited names rather than dotted
// paths. Must match the frontend "Copy" reference (destination-ui/marketo.ts
// `getReferenceString`) and the marketer's My Token, else Marketo rejects the
// trigger with "1003 Invalid token value".
const marketoToken = (name: string) => `{{my.${name}}}`;

export const marketoDefinition: DecisionEngineDestinationDefinition = {
  destinationType: "marketo",
  category: "trigger",
  generateSyncConfig: ({
    channelConfig,
    isPreview,
    sendToHightouchUser,
    previewRecipientMode,
    messageVariables = [],
    userColumns,
  }) => {
    const registeredPreview = isRegisteredPreviewSend({
      isPreview,
      previewRecipientMode,
    });
    const previewRecipientKey = getPreviewRecipientKey(channelConfig);
    return {
      type: "trigger",
      campaignId: { type: "standard", from: "campaign_id" },
      // Each variable and user column maps to its own scalar "my token".
      // The shared object blob other destinations use is rejected with error 609.
      // Message tags are intentionally not mapped: Marketo rejects the whole
      // trigger with 1003 "Invalid token value" for any token that isn't
      // pre-created as a My Token on the campaign's program.
      mappings: [
        ...uniq(messageVariables.map((variable) => variable.name)).map((name) =>
          getMappingForVariable(name, marketoToken(`ht_rec_var_${name}`)),
        ),
        ...userColumns.map((column) =>
          getMappingForUserColumn(
            column.column.toLowerCase(),
            marketoToken(
              `ht_user_${column.overridePropertyName ?? column.column}`,
            ),
          ),
        ),
        // The trace needs its own My Token override: Marketo tokens are flat
        // scalars, so it can't ride the variables object, and it is
        // deliberately not in messageVariables. Marketo rejects the trigger
        // unless the marketer has defined {{my.AidPreviewTraceId}} on the program.
        ...(registeredPreview
          ? [
              getMappingForVariable(
                PREVIEW_TRACE_ID_VARIABLE,
                marketoToken(PREVIEW_TRACE_ID_VARIABLE),
              ),
            ]
          : []),
      ],
      // Numeric keys are coerced because Marketo lead IDs are integers.
      // Registered mode without a key falls back to email keying even on
      // leadId channels: the registered recipient is the account address, so
      // external_id would otherwise be sent as a nonexistent lead ID.
      leadMapping:
        registeredPreview && previewRecipientKey
          ? {
              type: "static",
              to: "leadId",
              value: /^\d+$/.test(previewRecipientKey)
                ? Number(previewRecipientKey)
                : previewRecipientKey,
              valueType: /^\d+$/.test(previewRecipientKey)
                ? "NUMBER"
                : "STRING",
            }
          : registeredPreview
            ? { from: "external_id", to: "email" }
            : {
                from: "external_id",
                to:
                  channelConfig.recipientType === "leadId" &&
                  !(isPreview && sendToHightouchUser)
                    ? "leadId"
                    : "email",
              },
      // AID send volumes are large and hourly; the destination default of 10
      // rows per worker batch drains the trigger pipeline on every batch and
      // pays a lead-lookup call per 10 rows. With batchTriggers on, rows with
      // identical (campaign_id, token payload) within each 100-row worker batch
      // share a single trigger call; 100 still caps duplicate triggers from a
      // mid-batch worker restart at 100 sends.
      rowsPerBatch: 100,
      // Previews are single-row sends where per-call semantics matter
      // (createPreviewLead, registered-preview), so they stay on the per-row
      // trigger path.
      ...(isPreview ? {} : { batchTriggers: true }),
      // Triggers only fire for existing leads. In a synthetic preview the lead
      // may not exist yet (e.g. the synthetic inbox address), so allow the
      // destination to create any that are missing at trigger time. Registered
      // mode targets a lead the customer registered themselves and must never
      // pollute Marketo with created leads.
      ...(isPreview && !registeredPreview ? { createPreviewLead: true } : {}),
    };
  },
  // Overrides the deliberately tuned `rowsPerBatch: 100` above. 300 is
  // MAX_ROWS_PER_BATCH in the Marketo campaign-trigger destination, which
  // clamps at runtime regardless. Parallelization is hardcoded there.
  syncTuning: {
    batchSize: { configKey: "rowsPerBatch", max: 300 },
  },
};
